// API-Service für den Chatbot-Builder

// Typen
export interface Chatbot {
    id: string;
    name: string;
    description: string;
    status: 'draft' | 'training' | 'ready' | 'live';
    createdAt: string;
    lastUpdated: string;
    personality?: string;
    knowledgeSources?: KnowledgeSource[];
    appearance?: {
        primaryColor: string;
        chatBubbleStyle: 'rounded' | 'square';
        iconType: 'robot' | 'assistant' | 'custom';
    };
    settings?: {
        welcomeMessage: string;
        defaultLanguage: string;
        isActive: boolean;
        offlineMessage: string;
    };
    analytics?: {
        totalInteractions: number;
        averageRating: number;
        successRate: number;
    };
}

export interface KnowledgeSource {
    id: string;
    type: 'website' | 'document' | 'custom';
    source: string;
    title: string;
    status: 'pending' | 'processing' | 'processed' | 'failed';
}

// API-Basis-URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Helper für Fetch-Aufrufe
export const fetchWithAuth = async (endpoint: string, options: RequestInit = {}) => {
    // Auth-Token aus localStorage holen
    const authToken = localStorage.getItem('token');

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken ? `Bearer ${authToken}` : '',
            ...options.headers,
        },
    });

    if (!response.ok) {
        // Versuchen, die Fehlermeldung aus der Antwort zu extrahieren
        try {
            const errorData = await response.json();
            throw new Error(errorData.message || `API error: ${response.status} ${response.statusText}`);
        } catch (jsonError) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
    }

    return response.json();
};

// API-Funktionen
export const chatbotApi = {
    // Alle Chatbots abrufen
    async getAllChatbots(): Promise<Chatbot[]> {
        try {
            return await fetchWithAuth('/api/chatbots');
        } catch (error) {
            console.error('Failed to fetch chatbots:', error);
            throw error;
        }
    },

    // Einzelnen Chatbot abrufen
    async getChatbotById(id: string): Promise<Chatbot> {
        try {
            return await fetchWithAuth(`/api/chatbots/${id}`);
        } catch (error) {
            console.error(`Failed to fetch chatbot with id ${id}:`, error);
            throw error;
        }
    },

    // Neuen Chatbot erstellen
    async createChatbot(data: { name: string; description?: string }): Promise<Chatbot> {
        try {
            return await fetchWithAuth('/api/chatbots', {
                method: 'POST',
                body: JSON.stringify(data),
            });
        } catch (error) {
            console.error('Failed to create chatbot:', error);
            throw error;
        }
    },

    // Chatbot aktualisieren
    async updateChatbot(id: string, data: Partial<Chatbot>): Promise<Chatbot> {
        try {
            return await fetchWithAuth(`/api/chatbots/${id}`, {
                method: 'PUT',
                body: JSON.stringify(data)
            });
        } catch (error) {
            console.error(`Failed to update chatbot with id ${id}:`, error);
            throw error;
        }
    },

    // Chatbot löschen
    async deleteChatbot(id: string): Promise<void> {
        try {
            await fetchWithAuth(`/api/chatbots/${id}`, {
                method: 'DELETE'
            });
        } catch (error) {
            console.error(`Failed to delete chatbot with id ${id}:`, error);
            throw error;
        }
    },

    // Chatbot-Status aktualisieren
    async updateChatbotStatus(id: string, status: 'draft' | 'training' | 'ready' | 'live'): Promise<Chatbot> {
        try {
            return await fetchWithAuth(`/api/chatbots/${id}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status })
            });
        } catch (error) {
            console.error(`Failed to update status for chatbot ${id}:`, error);
            throw error;
        }
    },

    // Wissensquellen eines Chatbots abrufen
    async getKnowledgeSources(chatbotId: string): Promise<KnowledgeSource[]> {
        try {
            return await fetchWithAuth(`/api/chatbots/${chatbotId}/knowledge-sources`);
        } catch (error) {
            console.error(`Failed to fetch knowledge sources for chatbot ${chatbotId}:`, error);
            throw error;
        }
    },

    // Wissensquelle hinzufügen
    async addKnowledgeSource(
        chatbotId: string,
        data: { contentType: string; content: string; title?: string }
    ): Promise<KnowledgeSource> {
        try {
            return await fetchWithAuth(`/api/chatbots/${chatbotId}/knowledge-sources`, {
                method: 'POST',
                body: JSON.stringify(data)
            });
        } catch (error) {
            console.error(`Failed to add knowledge source to chatbot ${chatbotId}:`, error);
            throw error;
        }
    },

    // Wissensquelle entfernen
    async removeKnowledgeSource(knowledgeSourceId: string): Promise<void> {
        try {
            await fetchWithAuth(`/api/chatbots/knowledge-sources/${knowledgeSourceId}`, {
                method: 'DELETE'
            });
        } catch (error) {
            console.error(`Failed to remove knowledge source ${knowledgeSourceId}:`, error);
            throw error;
        }
    },

    // Wissensquelle verarbeiten
    async processKnowledgeSource(knowledgeSourceId: string): Promise<void> {
        try {
            await fetchWithAuth(`/api/chatbots/knowledge-sources/${knowledgeSourceId}/process`, {
                method: 'POST'
            });
        } catch (error) {
            console.error(`Failed to process knowledge source ${knowledgeSourceId}:`, error);
            throw error;
        }
    },

    // Chatbot-Vorschau generieren
    async generatePreview(chatbotId: string): Promise<{ previewUrl: string }> {
        try {
            return await fetchWithAuth(`/api/chatbots/${chatbotId}/preview`, {
                method: 'POST'
            });
        } catch (error) {
            console.error(`Failed to generate preview for chatbot ${chatbotId}:`, error);
            throw error;
        }
    },

    // Chatbot veröffentlichen
    async publishChatbot(chatbotId: string): Promise<{ embedCode: string; url: string }> {
        try {
            return await fetchWithAuth(`/api/chatbots/${chatbotId}/publish`, {
                method: 'POST'
            });
        } catch (error) {
            console.error(`Failed to publish chatbot ${chatbotId}:`, error);
            throw error;
        }
    },

    // Analytics aktualisieren
    async updateAnalytics(chatbotId: string, analytics: any): Promise<void> {
        try {
            await fetchWithAuth(`/api/chatbots/${chatbotId}/analytics`, {
                method: 'PUT',
                body: JSON.stringify({ analytics })
            });
        } catch (error) {
            console.error(`Failed to update analytics for chatbot ${chatbotId}:`, error);
            throw error;
        }
    }
};